/**
 * Circular seating geometry. SPEC.md §9.
 *
 * Seat positions are never persisted as x/y. The server stores logical
 * `seatIndex` values and every client derives pixel positions itself, rotating
 * the ring so the viewer sits at the bottom of their own phone. §9.1, §9.2
 */

export interface SeatSlot {
  seatIndex: number;
  /** Screen-space angle in degrees. 90 is the bottom of the ring. */
  angleDeg: number;
  /** Percentage offsets from the ring container's top-left corner. */
  xPct: number;
  yPct: number;
}

export interface RingLayoutInput {
  /** Canonical seat indexes of the players to place. Order is normalised here. */
  seatIndexes: readonly number[];
  /** The viewing player's seat, rotated to the bottom. Null renders unrotated. */
  viewerSeatIndex?: number | null;
  /** Ring radius as a percentage of the container's half-extent. */
  radiusPct?: number;
}

const DEFAULT_RADIUS_PCT = 38;

/**
 * Places occupied seats evenly around a circle, preserving canonical clockwise
 * order and rotating the viewer to the bottom.
 *
 * Occupied seats are distributed evenly rather than mapped to fixed slots, so a
 * removed player leaves no gap in the ring. In screen coordinates (y grows
 * downward) an increasing angle travels clockwise, so ascending seat indexes
 * read clockwise on every device — the property that makes each phone match the
 * real table. §9.2
 */
export function ringLayout(input: RingLayoutInput): SeatSlot[] {
  const { viewerSeatIndex = null, radiusPct = DEFAULT_RADIUS_PCT } = input;

  const ordered = [...new Set(input.seatIndexes)].sort((a, b) => a - b);
  const count = ordered.length;
  if (count === 0) return [];

  const viewerPos = viewerSeatIndex === null ? 0 : Math.max(0, ordered.indexOf(viewerSeatIndex));
  const step = 360 / count;

  return ordered.map((seatIndex, i) => {
    const relative = (i - viewerPos + count) % count;
    const angleDeg = 90 + relative * step;
    const rad = (angleDeg * Math.PI) / 180;
    return {
      seatIndex,
      angleDeg,
      xPct: 50 + radiusPct * Math.cos(rad),
      yPct: 50 + radiusPct * Math.sin(rad),
    };
  });
}

/** Lowest unused seat index, so joins fill gaps left by removals. */
export function nextFreeSeatIndex(taken: readonly number[]): number {
  const used = new Set(taken);
  let i = 0;
  while (used.has(i)) i += 1;
  return i;
}

export interface SeatAssignment {
  playerId: string;
  seatIndex: number;
}

/**
 * Moves one player to a seat, swapping with whoever holds it. §9.4
 * Returns only the assignments that changed, which is exactly what the server
 * broadcasts as the new authoritative order.
 */
export function moveToSeat(
  seats: readonly SeatAssignment[],
  movingPlayerId: string,
  targetSeatIndex: number,
): SeatAssignment[] {
  const moving = seats.find((s) => s.playerId === movingPlayerId);
  if (!moving) return [];
  if (moving.seatIndex === targetSeatIndex) return [];

  const occupant = seats.find(
    (s) => s.seatIndex === targetSeatIndex && s.playerId !== movingPlayerId,
  );

  const changes: SeatAssignment[] = [{ playerId: movingPlayerId, seatIndex: targetSeatIndex }];
  if (occupant) {
    changes.push({ playerId: occupant.playerId, seatIndex: moving.seatIndex });
  }
  return changes;
}

export interface DealerInput {
  /** The hand being dealt, 1-based. Null between hands, before the first deal. */
  handNumber: number | null | undefined;
  /** Everyone who could deal, in any order — canonical seat order is derived here. */
  seats: readonly SeatAssignment[];
  /**
   * The hand's frozen participants (§3.3). Omit to consider every seat given.
   * Passing them is what stops somebody sitting down mid-hand moving the button
   * that is already out.
   */
  participantIds?: readonly string[] | null;
  /**
   * How far the host has moved the button from where counting alone would put
   * it. §9.6
   *
   * Zero for every hand nobody has intervened in, which is almost all of them.
   * It is carried by the round rather than by the session so that a hand's
   * dealer is a fact about *that* hand: re-anchoring the rotation tonight must
   * not rewrite who dealt hand three, and history reads each round's own number.
   */
  dealerOffset?: number | null;
}

/**
 * The seats that could deal a hand, in the order the button walks them. §9.5
 *
 * Ascending `seatIndex` is clockwise on every device (§9.2), and filtering to the
 * hand's participants is what stops somebody sitting down mid-hand moving a
 * button that is already out (§3.3). Both halves are shared by the derivation
 * and by the offset that re-anchors it, so the two cannot disagree about which
 * list they are counting along. §60
 */
function dealerSeatOrder(
  seats: readonly SeatAssignment[],
  participantIds?: readonly string[] | null,
): SeatAssignment[] {
  const playing = participantIds ? new Set(participantIds) : null;
  return [...seats]
    .filter((seat) => !playing || playing.has(seat.playerId))
    .sort((a, b) => a.seatIndex - b.seatIndex);
}

/** JavaScript's `%` keeps the sign of the dividend; a seat index may not. */
function wrap(value: number, length: number): number {
  return ((value % length) + length) % length;
}

/**
 * Who deals a given hand. §9.5, §9.6
 *
 * The deal passes one seat clockwise every hand, so who holds it is a fact about
 * the seat order, the hand number and — when the host has moved it — one stored
 * number. It is derived here rather than stored for the same reason a score is:
 * a stored dealer is a second opinion waiting to disagree with the seating
 * everybody can see. §2, §60
 *
 * Hand 1 is dealt by the lowest seat at the table and each later hand by the next
 * seat clockwise, which is what ascending `seatIndex` means on every device. §9.2
 *
 * **The host's override does not break that, and the offset is why.** Naming a
 * dealer stores *where the count starts*, never *who is dealing* — so the button
 * still walks one seat per hand, every phone still computes the same answer from
 * the same three inputs, and the hand after the one the host re-anchored deals to
 * the seat clockwise of it without anybody having to store that too. Storing the
 * player instead would have been the second opinion this function exists to
 * avoid: the moment somebody was removed or reseated, the stored name and the
 * visible ring would disagree and nothing would say which was right. §9.6, §60
 *
 * The consequence of deriving it from the hand number is that this function
 * counts seats rather than remembering who last held the button: asked about a
 * hand whose ring has since changed size, it re-indexes. That is the honest
 * trade for never storing a dealer, and it is contained rather than lived with —
 * `nextDealerOffset` is what hands the button on between hands, and it steps by
 * seat against the ring as it now stands, so a player joining or leaving does
 * not cost anybody their turn. §9.6
 */
export function dealerPlayerId(input: DealerInput): string | null {
  const { handNumber, seats, participantIds, dealerOffset } = input;
  if (typeof handNumber !== 'number' || !Number.isFinite(handNumber) || handNumber < 1) {
    return null;
  }

  const ordered = dealerSeatOrder(seats, participantIds);
  if (ordered.length === 0) return null;

  const offset = typeof dealerOffset === 'number' && Number.isFinite(dealerOffset)
    ? Math.trunc(dealerOffset)
    : 0;

  return ordered[wrap(Math.trunc(handNumber) - 1 + offset, ordered.length)]?.playerId ?? null;
}

/**
 * The offset that makes `dealerPlayerId` land on a chosen player. §9.6
 *
 * This is the whole of "select dealer": the host names somebody, and what is
 * stored is the number that re-anchors the count so the existing rule produces
 * them. Nothing else about the derivation changes, which is what keeps one rule
 * rather than two — the client draws the button from the same function it always
 * did, and the server never has to write down a name that the ring could later
 * contradict. §60
 *
 * Returns null when the player cannot deal this hand: they are not seated, or
 * not among its frozen participants. The caller refuses rather than storing an
 * offset that would silently point at somebody else.
 *
 * It is deliberately the inverse of the derivation and is tested as a round
 * trip, because an off-by-one here is invisible until a table notices the button
 * skipped somebody.
 */
/**
 * The offset the *next* hand should be dealt with. §9.6, §9.5
 *
 * Copying the previous hand's offset forward is the obvious implementation and
 * it is wrong, because an offset is an index **modulo the ring size**. Carry 3
 * from a hand of four into a hand of five and it no longer means what it meant:
 * a table that overrides to the fourth seat and then gains a player deals the
 * next hand to that same player again, skipping the seat the button was owed
 * to. Which is precisely the mid-game join the override exists to fix, so the
 * feature would break on its own use case.
 *
 * So the inheritance is a derivation rather than a copy: work out who dealt the
 * previous hand, then answer "which seat is one clockwise of them *in the ring
 * as it now stands*", and store the offset that lands there. Nothing is
 * remembered between hands but the number, and the number is recomputed against
 * the table that actually exists. §60
 *
 * The step is taken by **seat index**, not by position in the list, so it is
 * still one seat clockwise when the previous dealer has since been removed —
 * their seat is gone but the seats either side of it are not.
 */
export function nextDealerOffset(input: {
  /** The hand just played: its number, its participants and its own offset. */
  previous: {
    handNumber: number;
    participantIds?: readonly string[] | null;
    dealerOffset?: number | null;
    /**
     * The seats that hand was played on, **including anyone since removed**.
     *
     * Re-deriving the previous dealer against today's ring is the subtle way to
     * get this wrong: a player leaving shortens the list, so the same arithmetic
     * that produced c during hand 3 produces d once b has gone, and the button
     * is handed on from somebody who never held it. Removed players keep their
     * row and their seat index precisely so the hand they played in can still be
     * reconstructed. §31, §9.5
     */
    seats: readonly SeatAssignment[];
  };
  /** The hand being dealt. */
  handNumber: number;
  /** Seats as they stand now. */
  seats: readonly SeatAssignment[];
  /** Who the new hand is between. §3.3 */
  participantIds?: readonly string[] | null;
}): number {
  const { previous, handNumber, seats, participantIds } = input;

  const ordered = dealerSeatOrder(seats, participantIds);
  if (ordered.length === 0) return 0;

  const previousDealer = dealerPlayerId({
    handNumber: previous.handNumber,
    seats: previous.seats,
    participantIds: previous.participantIds,
    dealerOffset: previous.dealerOffset,
  });
  // No previous hand to follow — the first deal of the night counts from zero.
  if (previousDealer === null) return 0;

  const previousSeat = previous.seats.find(
    (seat) => seat.playerId === previousDealer,
  )?.seatIndex;
  if (previousSeat === undefined) return 0;

  // One seat clockwise: the next seat index round the ring, wrapping to the
  // lowest. Taken against the new ring, which is what makes this survive a
  // player joining or leaving between the two hands.
  const next =
    ordered.find((seat) => seat.seatIndex > previousSeat) ?? ordered[0];
  if (!next) return 0;

  return (
    dealerOffsetFor({ handNumber, seats, participantIds, dealerPlayerId: next.playerId }) ?? 0
  );
}

export function dealerOffsetFor(
  input: DealerInput & { dealerPlayerId: string },
): number | null {
  const { handNumber, seats, participantIds } = input;
  if (typeof handNumber !== 'number' || !Number.isFinite(handNumber) || handNumber < 1) {
    return null;
  }

  const ordered = dealerSeatOrder(seats, participantIds);
  const index = ordered.findIndex((seat) => seat.playerId === input.dealerPlayerId);
  if (index < 0) return null;

  return wrap(index - (Math.trunc(handNumber) - 1), ordered.length);
}

/**
 * Rotates canonical seat order so the viewer leads. Useful for lists that must
 * read in the same direction as the ring (history, final score by seat).
 */
export function rotateForViewer<T>(
  items: readonly T[],
  viewerIndex: number,
): T[] {
  if (items.length === 0) return [];
  const start = ((viewerIndex % items.length) + items.length) % items.length;
  return [...items.slice(start), ...items.slice(0, start)];
}

/**
 * Avatar scale factor for crowded tables. Avatars must shrink rather than
 * overlap. §45
 */
export function avatarScaleForCount(count: number): number {
  if (count <= 6) return 1;
  if (count <= 8) return 0.9;
  if (count <= 10) return 0.8;
  return 0.72;
}

// --- The roster (§45, §14.1) -------------------------------------------------

/** Where one player's unit sits in the roster grid. */
export interface RosterSlot {
  /** 1-based grid row. */
  row: number;
  /**
   * 1-based start line in the track grid. A unit always spans **two** tracks.
   *
   * Two tracks per unit rather than one is the whole of how a short row is
   * centred: five players are three over two, and two units centred under three
   * need half a unit of clearance at each end. In whole units that is a
   * fractional column and needs an offset nobody can express in a grid; in half
   * units it is one track, and the arithmetic stays integers.
   */
  columnStart: number;
}

export interface RosterLayout {
  /** How many players each row holds, fullest first. */
  rowSizes: readonly number[];
  /** The widest row — how many units a row can hold. */
  columns: number;
  /** Grid tracks, which is two per unit. */
  tracks: number;
  /** One per player, in the order they were given. */
  slots: readonly RosterSlot[];
}

/**
 * How the roster arranges N players. §45, §18.2
 *
 * Four to a row at most, and the rows are filled as evenly as they divide with
 * the fuller ones first: two, three and four are one centred row, five is three
 * over two, seven is four over three, eight is four over four. A short last row
 * is centred rather than left-aligned, which is what stops five players reading
 * as a table of six with a gap in it.
 *
 * **Four fills its row rather than splitting into two of two**, and the reason is
 * the roster's height rather than its width. Two rows halve the row, and a unit
 * is a character over a name: the character is a square, so it can only ever use
 * the smaller of the two dimensions it is given. Split two by two, four players
 * get a cell half again as wide and a row half as tall — and the row is what
 * binds, at every viewport in §18.1. One row of four draws a visibly larger
 * character everywhere, which is the whole point of the arrangement. §45, §18.1
 *
 * It returns *placement*, not size. How big a unit ends up is a question about
 * the box the roster was given as well as the number of players in it, and the
 * only place both are known is the stylesheet — so the layout hands the grid its
 * row, column and track counts and lets `min()` against the container do the
 * rest. §18.3
 */
export function rosterLayout(count: number): RosterLayout {
  const players = Math.max(0, Math.trunc(count));
  if (players === 0) return { rowSizes: [], columns: 1, tracks: 2, slots: [] };

  // Four across is the point at which a name is still readable on a 320 px
  // screen; past that the roster gains a row rather than a column. §18.1, §19
  const rows = Math.ceil(players / 4);
  const base = Math.floor(players / rows);
  const remainder = players % rows;
  const rowSizes = Array.from({ length: rows }, (_, row) =>
    row < remainder ? base + 1 : base,
  );

  const columns = rowSizes[0] ?? 1;
  const slots: RosterSlot[] = [];
  rowSizes.forEach((size, row) => {
    // Whole tracks either side, because a unit is two of them. See `columnStart`.
    const lead = columns - size;
    for (let index = 0; index < size; index += 1) {
      slots.push({ row: row + 1, columnStart: 1 + lead + index * 2 });
    }
  });

  return { rowSizes, columns, tracks: columns * 2, slots };
}
